import {
  FileType,
  Emf, WarpType, WarpRequestServerPacket, WarpAgreeServerPacket,
  NpcRangeRequestClientPacket,
  PlayerRangeRequestClientPacket,
  RangeRequestClientPacket,
  WarpAcceptClientPacket,
  WarpTakeClientPacket,
  WelcomeAgreeClientPacket,
  WelcomeMsgClientPacket,
} from 'eolib';

import type { Client } from '@/client';
import { getEmf, saveEmf } from '@/db';
import { GameState } from '@/game-state';

type WarpState = {
  generation: number;
  sessionId: number;
  mapId: number;
  rid?: number[];
  size?: number;
  map: Emf | null;
  accepted: boolean;
  agreed: boolean;
  agreement: WarpAgreeServerPacket | null;
  reconnectRequired: boolean;
};

export class SessionController {
  private client: Client;
  private entryPending = false;
  private entryGeneration = 0;

  constructor(client: Client) {
    this.client = client;
    client.subscribeStateChanged(state => {
      if (state === GameState.Initial) { this.entryGeneration++; this.entryPending = false; this.warpGeneration++; this.warpState = null; clearTimeout(this.warpDeadline); }
    });
  }

  /**
   * A transport close invalidates the server-side warp handshake. Any map
   * download or atlas promise from that handshake must be ignored after the
   * browser reconnects; otherwise a late packet can leave the new login in a
   * permanent "can't load world" loop.
   */
  abortPendingWarp(): void {
    // A dropped transport invalidates login preloading too. Auto-reconnect
    // need not pass through Initial, so its old entry promise must not block
    // or send WelcomeMsg using a replacement connection's session.
    this.entryGeneration++;
    this.entryPending = false;
    this.warpGeneration++;
    clearTimeout(this.warpDeadline);
    this.warpDeadline = undefined;
    this.warpState = null;
    this.client.warpQueued = false;
    this.client.releaseWarpFrame();
    this.client.worldLoading = false;
    this.client.worldLoadError = '';
    this.client.keyboardController.unfreeze();
  }

  private warpGeneration = 0;
  private warpState: WarpState | null = null;
  private warpDeadline?: ReturnType<typeof setTimeout>;

  private showWarpLoading(message: string): void {
    this.client.preserveWarpFrame();
    this.client.worldLoading = true;
    this.client.worldLoadError = '';
    this.client.keyboardController.freeze();
    this.client.npcController.clearAllTargets();
    this.client.movementController.autoWalkPath = [];
    this.client.setLoginPending(true, message);
  }

  private failWarp(generation: number, error: unknown): void {
    if (generation !== this.warpGeneration || !this.warpState) return;
    clearTimeout(this.warpDeadline);
    console.error('Destination loading failed', error);
    if (this.warpState.accepted && !this.warpState.agreed) this.warpState.reconnectRequired = true;
    const detail = error instanceof Error ? error.message : String(error);
    this.client.worldLoadError = this.warpState.reconnectRequired
      ? 'The warp session changed or expired. Retry to return to login and reconnect.'
      : `Area ${this.warpState.mapId} could not finish loading: ${detail}. Retry or return to login.`;
    this.client.setLoginPending(false);
  }

  async beginWarp(packet: WarpRequestServerPacket): Promise<void> {
    const generation = ++this.warpGeneration;
    clearTimeout(this.warpDeadline);
    this.client.sessionId = packet.sessionId;
    this.client.warpMapId = packet.mapId;
    this.client.warpQueued = false;
    const data = packet.warpTypeData as WarpRequestServerPacket.WarpTypeDataMapSwitch;
    this.warpState = { generation, sessionId: packet.sessionId, mapId: packet.mapId, map: null,
      accepted: false, agreed: false, agreement: null, reconnectRequired: false,
      ...(packet.warpType === WarpType.MapSwitch ? { rid: data.mapRid, size: data.mapFileSize } : {}) };
    this.showWarpLoading('Loading your destination…');
    this.armWarpDeadline(generation);
    try {
      const map = packet.warpType === WarpType.Local ? this.client.map : await getEmf(packet.mapId);
      if (generation !== this.warpGeneration || !this.warpState) return;
      const expected = this.warpState;
      if (map && (!expected.rid || (map.rid[0] === expected.rid[0] && map.rid[1] === expected.rid[1] && map.byteSize === expected.size))) {
        expected.map = map;
        this.client.warpQueued = true;
      } else this.requestWarpMap(packet.mapId);
    } catch (error) {
      if (generation === this.warpGeneration) this.requestWarpMap(packet.mapId);
    }
  }

  receiveWarpMap(map: Emf): void {
    const pending = this.warpState;
    if (!pending || pending.accepted || pending.agreed || pending.reconnectRequired) return;
    if (pending.rid && (map.rid[0] !== pending.rid[0] || map.rid[1] !== pending.rid[1])) {
      // WarpMap has no session/map ID. Do not relabel an unmatched download or
      // retry forever against an obsolete advertised revision.
      pending.reconnectRequired = true;
      this.failWarp(pending.generation, new Error('Destination revision changed during transfer'));
      return;
    }
    // Use the exact server-delivered object immediately. Persistent cache is optional.
    pending.map = map;
    void saveEmf(pending.mapId, map).catch(error => console.warn('Map cache unavailable; using downloaded map', error));
    this.client.warpQueued = true;
  }

  async finishWarp(packet: WarpAgreeServerPacket): Promise<void> {
    const pending = this.warpState;
    // An agreement cannot complete a request we have not accepted. Ignore
    // duplicate agreements while the accepted destination is being prepared.
    if (!pending || !pending.accepted || pending.agreed) return;
    pending.agreed = true;
    pending.agreement = packet;
    clearTimeout(this.warpDeadline);
    try { await this.completeWarp(pending); }
    catch (error) { this.failWarp(pending.generation, error); }
  }

  private async completeWarp(pending: WarpState): Promise<void> {
    if (pending !== this.warpState || !pending.agreement) return;
    const packet = pending.agreement;
    const destination = packet.warpTypeData instanceof WarpAgreeServerPacket.WarpTypeDataMapSwitch ? packet.warpTypeData.mapId : pending.mapId;
    this.showWarpLoading('Loading your destination…');
    // Retry the entire agreed destination installation, including a rescue map
    // that was unavailable on the first attempt, before preparing its graphics.
    const map = destination !== pending.mapId || !pending.map
      ? await getEmf(destination)
      : pending.map;
    if (pending !== this.warpState) return;
    if (!map) throw new Error('Destination map missing');
    pending.map = map;
    pending.mapId = destination;
    this.client.nearby = packet.nearby;
    this.client.mapId = destination;
    this.client.warpMapId = destination;
    this.client.setMap(map);
    await this.prepareWarpGraphics(pending.generation);
  }

  private async prepareWarpGraphics(generation: number): Promise<void> {
    this.showWarpLoading('Preparing map and character graphics…');
    await this.client.atlas.prepareMap();
    if (generation !== this.warpGeneration || !this.warpState) return;
    this.client.mapRenderer.buildCaches();
    this.client.releaseWarpFrame(true);
    this.client.worldLoading = false;
    this.client.worldLoadError = '';
    this.warpState = null;
    clearTimeout(this.warpDeadline);
    this.client.setLoginPending(false);
    this.client.keyboardController.unfreeze();
  }

  async retryWorldLoading(): Promise<void> {
    const pending = this.warpState;
    if (!pending) return this.enterGame();
    if (pending.reconnectRequired || (pending.accepted && !pending.agreed)) {
      // There is no client packet to renegotiate this warp's revision/session.
      // A fresh login is safer than replaying an ambiguous acceptance.
      this.client.disconnect();
      return;
    }
    try {
      if (pending.agreed) await this.completeWarp(pending);
      else if (!pending.accepted) {
        clearTimeout(this.warpDeadline);
        pending.map = null;
        this.client.warpQueued = false;
        this.showWarpLoading('Downloading the destination again…');
        this.requestWarpMap(pending.mapId);
        this.armWarpDeadline(pending.generation);
      }
    } catch (error) { this.failWarp(pending.generation, error); }
  }

  private armWarpDeadline(generation: number): void {
    this.warpDeadline = setTimeout(() => {
      if (generation !== this.warpGeneration || !this.warpState) return;
      // A dead/mismatched handshake cannot be repaired by sending WarpTake
      // over and over. Retry must get a fresh login instead of looping.
      this.warpState.reconnectRequired = true;
      this.failWarp(generation, new Error('Destination response timed out'));
    }, 30000);
  }

  requestWarpMap(id: number): void {
    const packet = new WarpTakeClientPacket();
    packet.sessionId = this.warpState?.sessionId ?? this.client.sessionId;
    packet.mapId = id;
    this.client.bus!.send(packet);
  }

  acceptWarp(): void {
    const pending = this.warpState;
    if (!pending || !pending.map || pending.accepted || pending.agreed || pending.reconnectRequired) { this.client.warpQueued = false; return; }
    pending.accepted = true;
    this.client.npcController.cancelAutoAttack();
    this.client.movementController.autoWalkPath = [];

    const packet = new WarpAcceptClientPacket();
    packet.sessionId = pending.sessionId;
    packet.mapId = pending.mapId;
    this.client.bus!.send(packet);
    this.client.warpQueued = false;
    this.client.keyboardController.freeze();
  }

  requestFile(fileType: FileType, id: number): void {
    const packet = new WelcomeAgreeClientPacket();
    packet.sessionId = this.client.sessionId;
    packet.fileType = fileType;

    switch (fileType) {
      case FileType.Ecf:
        packet.fileTypeData = new WelcomeAgreeClientPacket.FileTypeDataEcf();
        packet.fileTypeData.fileId = id;
        break;
      case FileType.Eif:
        packet.fileTypeData = new WelcomeAgreeClientPacket.FileTypeDataEif();
        packet.fileTypeData.fileId = id;
        break;
      case FileType.Enf:
        packet.fileTypeData = new WelcomeAgreeClientPacket.FileTypeDataEnf();
        packet.fileTypeData.fileId = id;
        break;
      case FileType.Esf:
        packet.fileTypeData = new WelcomeAgreeClientPacket.FileTypeDataEsf();
        packet.fileTypeData.fileId = id;
        break;
      case FileType.Emf:
        packet.fileTypeData = new WelcomeAgreeClientPacket.FileTypeDataEmf();
        packet.fileTypeData.fileId = id;
        break;
    }

    this.client.bus!.send(packet);
  }

  async enterGame(): Promise<void> {
    if (this.entryPending) return;
    this.entryPending = true;
    const generation = this.entryGeneration;
    this.client.worldLoading = true;
    this.client.worldLoadError = '';
    this.client.setLoginPending(true, `Loading ${this.client.map?.name || 'the world'}…`);
    try {
      await this.client.atlas.prepareMap();
      if (generation !== this.entryGeneration) return;
      const packet = new WelcomeMsgClientPacket();
      packet.characterId = this.client.characterId;
      packet.sessionId = this.client.sessionId;
      this.client.setLoginPending(true, 'Entering the world…');
      this.client.bus!.send(packet);
    } catch (error) {
      if (generation !== this.entryGeneration) return;
      console.error('World preload failed', error);
      this.client.disconnect();
      this.client.alertController.show(
        'Could not prepare the world',
        'You have been returned to login instead of retrying the same failed world load. Please sign in again or contact support if it happens again.',
      );
    } finally {
      if (generation === this.entryGeneration) this.entryPending = false;
    }
  }

  rangeRequest(playerIds: number[], npcIndexes: number[]): void {
    const packet = new RangeRequestClientPacket();
    packet.playerIds = playerIds;
    packet.npcIndexes = npcIndexes;
    this.client.bus!.send(packet);
  }

  requestCharacterRange(playerIds: number[]): void {
    const packet = new PlayerRangeRequestClientPacket();
    packet.playerIds = playerIds;
    this.client.bus!.send(packet);
  }

  requestNpcRange(npcIndexes: number[]): void {
    const packet = new NpcRangeRequestClientPacket();
    packet.npcIndexes = npcIndexes;
    this.client.bus!.send(packet);
  }
}
